import { ActionTree } from 'vuex';
import { AttendeeCourseState } from './types';
import { RootState } from '../../types';
import { defaultBackendAttendeeCourse } from '@/shared/backend/attendeeCourse';
import { Logger } from 'fsts';
import moduleUtils from '../moduleUtils';
import { Value, AttendeeCourse } from '@/shared/model/attendeeCourse';
import { AxiosResponse } from 'axios';
import { i18nGlobal } from '@/i18n';
import DownloadUtils from '@/shared/utils/downloadUtils';

const logger = new Logger('actions.attendeeCourses');
export const actions: ActionTree<AttendeeCourseState, RootState> = {
  getAttendeeCourses(
    { commit, dispatch, getters, rootGetters },
    payload?: {
      searchParams?: any;
      attendeeId?: string;
      dateTimeId?: string;
      noStore?: boolean;
      cancelled?: boolean;
    }
  ) {
    commit('setAttendeeCoursesIsLoading', true);
    let searchParams =
      payload?.searchParams ?? getters.getAttendeeCoursesSearchParams;

    return defaultBackendAttendeeCourse
      .getAttendeeCourses(
        searchParams,
        payload?.attendeeId,
        payload?.dateTimeId,
        payload?.cancelled
      )
      .then((response: AxiosResponse<Value>) => {
        commit('setAttendeeCoursesIsLoading', false);
        if (payload?.noStore) return response.data;

        commit('setAttendeeCourses', response.data);
        return response.data;
      })
      .catch((e: any) => {
        commit('setAttendeeCoursesIsLoading', false);
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  getAttendeeCourse({ commit, dispatch }, id: string) {
    return defaultBackendAttendeeCourse
      .getAttendeeCourse(id)
      .then((response: AxiosResponse<AttendeeCourse>) => {
        return response.data;
      })
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  updateAttendeeCourse({ commit, dispatch }, file: AttendeeCourse) {
    return defaultBackendAttendeeCourse
      .updateAttendeeCourse(file)
      .then(
        (
          response: AxiosResponse<{
            errors: [];
            id: string;
            isValid: boolean;
            result: AttendeeCourse;
          }>
        ) => {
          moduleUtils.ok(i18nGlobal.t(`success.attendeeCourse_update`), commit);
          return response.data;
        }
      )
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  updateAttendeeCourseMultiple({ commit, dispatch }, items: AttendeeCourse[]) {
    commit('setAttendeeCoursesIsLoading', true);
    return defaultBackendAttendeeCourse
      .updateAttendeeCourseMultiple(items, i18nGlobal.locale.value)
      .then(
        (
          response: AxiosResponse<{
            errors: [];
            id: string;
            isValid: boolean;
            result: AttendeeCourse;
          }>
        ) => {
          moduleUtils.ok(i18nGlobal.t(`success.attendeeCourse_update`), commit);
          return response.data;
        }
      )
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      })
      .finally(() => {
        commit('setAttendeeCoursesIsLoading', false);
      });
  },
  deleteAttendeeCourse({ commit, dispatch }, id: string) {
    return defaultBackendAttendeeCourse
      .deleteAttendeeCourse(id)
      .then(
        (
          response: AxiosResponse<{
            errors: [];
            id: string;
            isValid: boolean;
            result: AttendeeCourse;
          }>
        ) => {
          return response;
        }
      )
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  async downloadPdf({ commit }, dateTimeId: string) {
    const lang = i18nGlobal.locale.value;
    try {
      await defaultBackendAttendeeCourse
        .downloadPdf(dateTimeId, lang)
        .then((response) => {
          const fileName = DownloadUtils.getFileName(
            response.headers['content-disposition']
          );
          const type = response.headers['content-type'];
          if (DownloadUtils.isIeOrEdge(window)) {
            const file = new File([response.data], fileName, {
              type: type,
            });
            (window.navigator as any).msSaveOrOpenBlob(file, fileName);
          } else {
            const blob = new Blob([response.data], { type: type });
            const url = DownloadUtils.createDownloadLink(blob, fileName);
            window.URL.revokeObjectURL(url);
          }
        });
    } catch (e) {
      logger.error(e);
      moduleUtils.error(i18nGlobal.t(`error.download_file`), commit, e, logger);
      throw e;
    }
  },
};
